import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * Tweens a number from its current displayed value to `target` using rAF.
 * Useful for KPI count-ups, score changes, etc.
 *
 * Honors `prefers-reduced-motion` (jumps straight to the target).
 */
export function useAnimatedNumber(target: number, durationMs = 500): number {
  const prefersReduced = useReducedMotion();
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    if (prefersReduced) {
      fromRef.current = target;
      setDisplay(target);
      return;
    }
    if (target === display) return;
    const from = display;
    fromRef.current = from;
    const start = performance.now();
    let raf = 0;
    function step(now: number) {
      const elapsed = now - start;
      const t = Math.min(elapsed / durationMs, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (target - from) * eased;
      setDisplay(target >= from ? Math.min(target, next) : Math.max(target, next));
      if (t < 1) raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // We deliberately don't include `display` so each new target restarts a tween from the *current* shown value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs, prefersReduced]);

  return display;
}
